// Core SDK media, with explicit readiness checks (SDK init can swallow denial).
import RealtimeKitClient from "@cloudflare/realtimekit";
import { TEST_MODE, attachTestMeeting } from "./test-media.js";
import { crackleWorkaround, FORCE_IOS27 } from "./platform.js";

export class Call {
  constructor(opts) {
    this.opts = opts;
    this.meeting = null;
    this.remote = null;
    this.remoteTrack = null;
    this.closed = false;
    this.ready = false;
    this.terminal = null;
    this.permissionFailures = new Map();
    this.tracks = new Set();
    this.trackListeners = new Map();
    this.listeners = [];
    this.mediaEvents = TEST_MODE ? [] : null;
    this.mediaStartedAt = TEST_MODE ? Date.now() : null;
    this.releasesPending = 0;
  }

  // Identifier-free diagnostics for the existing synthetic-failure seam only.
  testMediaSnapshot() {
    if (!TEST_MODE) return null;
    const track = (t) => t ? { state: t.readyState, enabled: t.enabled, muted: t.muted } : null;
    const participant = (p) => ({
      attached: p === this.remote, audioEnabled: p.audioEnabled, videoEnabled: p.videoEnabled,
      audioTrack: track(p.audioTrack), videoTrack: track(p.videoTrack),
      producers: (p.producers || []).map((producer) => ({
        kind: producer.kind, paused: producer.pause ?? null,
        consumer: producer.consumer ? { kind: producer.consumer.kind ?? null,
          paused: producer.consumer.paused ?? null } : null,
      })),
    });
    const joined = this.meeting?.participants.joined.toArray() || [];
    const audio = this.opts.remoteAudio, video = this.opts.remoteVideo;
    return {
      closed: this.closed, ready: this.ready, terminal: !!this.terminal,
      releasesPending: this.releasesPending, releaseFailure: this.releaseFailure ?? null, joinedCount: joined.length,
      remoteInJoined: !!this.remote && joined.includes(this.remote),
      remote: this.remote ? participant(this.remote) : null,
      joined: joined.map(participant),
      playback: { audioPaused: audio.paused, videoHidden: video.hidden,
        audioTracks: audio.srcObject?.getAudioTracks().map(track) || [],
        videoTracks: video.srcObject?.getVideoTracks().map(track) || [],
        audioMatchesRemote: !!this.remoteTrack && this.remoteTrack === this.remote?.audioTrack,
        audioElementMatchesRemote: !!this.remote?.audioTrack &&
          !!audio.srcObject?.getAudioTracks().includes(this.remote.audioTrack),
        videoElementMatchesRemote: !!this.remote?.videoTrack &&
          !!video.srcObject?.getVideoTracks().includes(this.remote.videoTrack) },
      events: this.mediaEvents.slice(),
    };
  }

  noteMediaEvent(event, p = this.remote) {
    if (!TEST_MODE) return;
    this.mediaEvents.push({ event, atMs: Date.now() - this.mediaStartedAt, pageMs: performance.now(),
      attached: !!p && p === this.remote,
      joinedCount: this.meeting?.participants.joined.size ?? 0,
      audioEnabled: p?.audioEnabled ?? null, videoEnabled: p?.videoEnabled ?? null,
      audioTrackState: p?.audioTrack?.readyState ?? null,
      videoTrackState: p?.videoTrack?.readyState ?? null });
    if (this.mediaEvents.length > 60) this.mediaEvents.shift();
  }

  unlockAudio() {
    if (this.closed) return;
    const el = this.opts.remoteAudio;
    el.srcObject = new MediaStream();
    el.play().catch(() => {});
  }

  setRemoteAudio(track) {
    if (this.closed || track === this.remoteTrack) return;
    this.remoteTrack = track;
    const el = this.opts.remoteAudio;
    el.srcObject = track ? new MediaStream([track]) : null;
    if (!track) return;
    el.play().catch((e) => {
      if (!this.closed && this.remoteTrack === track && e?.name === "NotAllowedError") {
        this.opts.onAutoplayBlocked?.();
      }
    });
  }

  listen(target, event, callback) {
    target.on(event, callback);
    this.listeners.push(() => target.removeListener(event, callback));
  }

  collectTracks(self) {
    for (const track of [self.audioTrack, self.rawAudioTrack, self.videoTrack, self.rawVideoTrack]) {
      if (!track || this.tracks.has(track)) continue;
      this.tracks.add(track);
      const ended = () => {
        if (this.closed || !this.ready || this.meeting?.self !== self) return;
        // A device switch can end a replaced track; check the current tracks.
        try { this.assertMediaReady(); } catch { this.fail("media_ended"); }
      };
      track.addEventListener("ended", ended);
      this.trackListeners.set(track, ended);
    }
  }

  assertMediaReady() {
    if (this.closed || this.terminal || !this.meeting) {
      throw new Error(this.terminal || "call abandoned");
    }
    const self = this.meeting.self;
    this.collectTracks(self);
    for (const kind of this.opts.mode === "video" ? ["audio", "video"] : ["audio"]) {
      const permission = self.mediaPermissions?.[kind];
      const failure = this.permissionFailures.get(kind);
      if (failure || permission !== "ACCEPTED") {
        const err = new Error(`${kind} permission ${failure || permission || "not ready"}`);
        err.name = /DENIED|CANCELED/.test(failure || permission || "") ? "NotAllowedError" : "MediaNotReadyError";
        throw err;
      }
      const track = self[`${kind}Track`];
      const raw = self[kind === "audio" ? "rawAudioTrack" : "rawVideoTrack"];
      if (!self[`${kind}Enabled`] || !track || track.kind !== kind ||
          track.readyState !== "live" || !track.enabled ||
          !raw || raw.kind !== kind || raw.readyState !== "live" || !raw.enabled) {
        const err = new Error(`${kind} is not ready`);
        err.name = "MediaNotReadyError";
        throw err;
      }
    }
    return true;
  }

  fail(state) {
    if (this.closed || this.terminal) return;
    this.terminal = state;
    this.ready = false;
    this.opts.onLeft?.(state);
  }

  async join(authToken) {
    if (this.closed) throw new Error("call abandoned");
    if (this.joining) return this.joining;
    this.joining = this.start(authToken);
    return this.joining;
  }

  async start(authToken) {
    const { mode, remoteVideo, selfVideo } = this.opts;
    const video = mode === "video";
    const noEchoCancellation = !video && (FORCE_IOS27 || crackleWorkaround());
    try {
      this.noteMediaEvent("init-start");
      const m = await RealtimeKitClient.init({
        authToken,
        defaults: {
          audio: true, video,
          ...(noEchoCancellation ? { mediaConfiguration: { audio: { echoCancellation: false } } } : {}),
        },
      });
      if (this.closed) {
        this.releaseMeeting(m);
        throw new Error("call abandoned");
      }
      this.meeting = m;
      this.noteMediaEvent("init-complete");
      if (TEST_MODE) attachTestMeeting(m);
      const active = () => !this.closed && !this.terminal && this.meeting === m;
      this.listen(m.self, "roomLeft", ({ state }) => { if (active()) this.fail(state); });
      this.listen(m.self, "autoplayError", () => { if (active()) this.opts.onAutoplayBlocked?.(); });
      const check = () => {
        if (!active()) return;
        this.collectTracks(m.self);
        if (this.ready) {
          try { this.assertMediaReady(); } catch { this.fail("media_ended"); }
        }
      };
      this.listen(m.self, "audioUpdate", check);
      if (video) this.listen(m.self, "videoUpdate", check);
      const permission = ({ kind, message }) => {
        if (!active() || (kind !== "audio" && !(video && kind === "video"))) return;
        if (message === "ACCEPTED") this.permissionFailures.delete(kind);
        else {
          this.permissionFailures.set(kind, message || "not ready");
          if (this.ready) this.fail("permission_denied");
        }
      };
      this.listen(m.self, "mediaPermissionUpdate", permission);
      this.listen(m.self, "mediaPermissionError", permission);
      this.assertMediaReady(); // don't enter the room with unusable media

      if (video) {
        try {
          const cams = await m.self.getVideoDevices();
          if (!active()) throw new Error("call abandoned");
          const front = cams.find((d) => /front|user|facetime/i.test(d.label));
          if (front) await m.self.setDevice(front);
        } catch (e) {
          if (!active()) { this.releaseMeeting(m); throw e; }
          console.warn("camera selection", e);
        }
        if (!active()) { this.releaseMeeting(m); throw new Error("call abandoned"); }
        this.assertMediaReady();
        m.self.registerVideoElement(selfVideo, true);
        selfVideo.hidden = false;
      }

      const attach = (p) => {
        this.noteMediaEvent("participant-attach", p);
        if (!active() || p === this.remote) return;
        if (video) this.remote?.deregisterVideoElement(remoteVideo);
        this.remote = p;
        this.setRemoteAudio(p.audioEnabled ? p.audioTrack : null);
        this.listen(p, "audioUpdate", ({ audioEnabled, audioTrack }) => {
          this.noteMediaEvent("remote-audio-update", p);
          if (active() && this.remote === p) this.setRemoteAudio(audioEnabled ? audioTrack : null);
        });
        if (!video) return;
        p.registerVideoElement(remoteVideo);
        remoteVideo.hidden = !p.videoEnabled;
        this.listen(p, "videoUpdate", ({ videoEnabled }) => {
          this.noteMediaEvent("remote-video-update", p);
          if (active() && this.remote === p) remoteVideo.hidden = !videoEnabled;
        });
      };
      this.listen(m.participants.joined, "participantJoined", attach);
      if (TEST_MODE) this.listen(m.participants.joined, "participantsCleared", () => {
        this.noteMediaEvent("participants-cleared");
      });
      this.listen(m.participants.joined, "participantLeft", (p) => {
        this.noteMediaEvent("participant-left", p);
        if (!active() || p !== this.remote) return;
        if (video) p.deregisterVideoElement(remoteVideo);
        this.remote = null;
        this.setRemoteAudio(null);
        remoteVideo.hidden = true;
      });
      await m.join();
      this.noteMediaEvent("join-complete");
      // leave() may have run while the SDK was still completing join.
      if (!active()) { this.releaseMeeting(m); throw new Error("call abandoned"); }
      this.assertMediaReady(); // join success alone is not permission success
      this.ready = true;
      for (const p of m.participants.joined.toArray()) attach(p);
    } catch (e) {
      this.leave();
      throw e;
    }
  }

  async enableSound() {
    if (this.closed) return false;
    try { await this.opts.remoteAudio.play(); return !this.closed; }
    catch (e) { console.warn("audio play", e); return false; }
  }

  releaseMeeting(m) {
    this.noteMediaEvent("release-start");
    // Release hardware before awaiting SDK transport cleanup.
    for (const track of [m.self.audioTrack, m.self.rawAudioTrack, m.self.videoTrack, m.self.rawVideoTrack]) {
      try { track?.stop(); } catch {}
    }
    // SDK leave performs ordered audio/video shutdown. Calling disable methods
    // concurrently aborts it with ERR1100 before the old transport can leave.
    try {
      ++this.releasesPending;
      return Promise.resolve(m.leave()).catch((e) => {
        this.noteMediaEvent("release-rejected");
        if (TEST_MODE) this.releaseFailure = { name: e?.name ?? "Error", code: e?.code ?? null,
          message: String(e?.message ?? "").replace(/https?:\S+|[A-Za-z0-9_-]{40,}/g, "[redacted]").slice(0, 160) };
      }).finally(() => {
        --this.releasesPending;
        this.noteMediaEvent("release-settled");
      });
    } catch {
      --this.releasesPending;
      this.noteMediaEvent("release-threw");
    }
  }

  leave() {
    if (this.closed) return;
    this.closed = true;
    this.ready = false;
    const m = this.meeting;
    this.meeting = null;
    for (const off of this.listeners.splice(0)) { try { off(); } catch {} }
    for (const [track, ended] of this.trackListeners) track.removeEventListener("ended", ended);
    this.trackListeners.clear();
    for (const track of this.tracks) { try { track.stop(); } catch {} }
    this.tracks.clear();
    try { this.remote?.deregisterVideoElement(this.opts.remoteVideo); } catch {}
    try { m?.self.deregisterVideoElement(this.opts.selfVideo); } catch {}
    this.remote = null;
    this.remoteTrack = null;
    this.opts.remoteAudio.pause();
    this.opts.remoteAudio.srcObject = null;
    for (const v of [this.opts.remoteVideo, this.opts.selfVideo]) {
      v.hidden = true;
      v.srcObject = null;
    }
    if (m) return this.releaseMeeting(m);
  }
}
